/**
 * Contact Detail Page
 *
 * Complete detail view for a single contact record with 7 tabs
 */

import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { persistedContactRecords } from '../../services/mockData/contacts';
import { getRecordById, deleteRecord } from '../../services/recordStorageService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { DetailPageLayout } from '../../components/detail/DetailPageLayout';
import { DetailHeader } from '../../components/detail/DetailHeader';
import { DetailTabs } from '../../components/detail/DetailTabs';
import { LayoutSection } from '../../components/detail/LayoutSection';
import { TasksTab } from '../../components/detail/TasksTab';
import { usePageLayout, usePageLayoutTabs } from '../../hooks/useObjectManager';
import { ProductsTab } from '../../components/detail/ProductsTab';
import { FilesTab } from '../../components/detail/FilesTab';
import { HistoryTab } from '../../components/detail/HistoryTab';
import { ActivityTab } from '../../components/detail/ActivityTab';
import { ContactModal } from '../../components/modals/ContactModal';
import { LogCallModal } from '../../components/modals/LogCallModal';
import { LogMeetingModal } from '../../components/modals/LogMeetingModal';
import { LogTaskModal } from '../../components/modals/LogTaskModal';
import { AddNoteModal } from '../../components/modals/AddNoteModal';
import { SendEmailModal } from '../../components/modals/SendEmailModal';
import { EditActivityModal } from '../../components/modals/EditActivityModal';
import { Activity } from '../../types/activity';
import { activityStorage } from '../../services/activityStorage';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { ModalType } from '../../services/queries/buttonActionQueries';
import { Mail } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { isFeatureEnabled } from '../../config/mvpConfig';

interface ContactDetailPageProps {
  id: string;
  onBack: () => void;
  onNavigateToClient?: (clientId: string) => void;
}

export const ContactDetailPage: React.FC<ContactDetailPageProps> = ({ id, onBack, onNavigateToClient }) => {
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();
  const { userId } = useCurrentUser();
  const allTabs = usePageLayoutTabs('Contact__c');
  const tabs = allTabs.filter(
    (t: { id: string }) => t.id !== 'products' || isFeatureEnabled('showProductsTab')
  ).filter(
    (t: { id: string }) => t.id !== 'files' || isFeatureEnabled('showFilesTab')
  );
  const [activeTab, setActiveTab] = useState(tabs[0]?.id ?? 'overview');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);
  const [deletingActivity, setDeletingActivity] = useState<Activity | null>(null);
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);

  const { data: contact, isLoading } = useQuery({
    queryKey: ['contact', id],
    queryFn: async () => {
      if (shouldUseGobodo()) {
        const record = await getRecordById('Contact', id);
        if (!record) throw new Error('Contact not found');
        return { ...record, name: record.name ?? record.label ?? (record as any).Name__c };
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      const allContacts = persistedContactRecords.get();
      const found = allContacts.find(c => c.id === id);
      if (!found) throw new Error('Contact not found');
      return found;
    }
  });

  // Fetch page layout (uses Record Pages assignment system)
  const { data: pageLayout, isLoading: loadingLayout } = usePageLayout('Contact__c', contact);

  const handleEdit = () => {
    setIsModalOpen(true);
  };

  const handleDelete = () => {
    const displayName = contact?.name ?? (contact as any)?.Name__c ?? contact?.label ?? id;
    if (!window.confirm(`Are you sure you want to delete ${displayName}?`)) return;
    if (shouldUseGobodo()) {
      deleteRecord('Contact', id)
        .then((ok) => {
          if (ok) {
            queryClient.invalidateQueries({ queryKey: ['contactListViewData'] });
            queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Contact__c'] });
            success('Contact deleted', `${displayName} has been deleted`);
            onBack();
          }
        })
        .catch(() => success('Error', 'Failed to delete contact'));
      return;
    }
    const allContacts = persistedContactRecords.get();
    persistedContactRecords.set(allContacts.filter(c => c.id !== id));
    success('Contact deleted', `${displayName} has been deleted`);
    onBack();
  };

  // Activity edit/delete handlers
  const handleActivityChanged = () => {
    setActivityRefreshKey(prev => prev + 1);
  };

  const handleEditActivity = (activity: Activity) => {
    setEditingActivity(activity);
  };

  const handleDeleteActivity = (activity: Activity) => {
    setDeletingActivity(activity);
  };

  const confirmDeleteActivity = async () => {
    if (!deletingActivity) return;
    try {
      const deleted = await activityStorage.delete(deletingActivity.id);
      if (deleted) {
        success('Activity deleted', 'The activity has been removed');
        handleActivityChanged();
      } else {
        showError('Delete failed', 'Could not delete the activity');
      }
    } catch (error) {
      console.error('[ContactDetailPage] Delete activity failed:', error);
      showError('Delete failed', 'Could not delete the activity');
    }
    setDeletingActivity(null);
  };

  // Handle modal opening from Object Manager buttons
  const handleOpenModal = (modalType: ModalType) => {
    switch (modalType) {
      case 'send-email':
        setShowEmailModal(true);
        break;
      case 'log-call':
        setShowCallModal(true);
        break;
      case 'log-meeting':
        setShowMeetingModal(true);
        break;
      case 'create-task':
        setShowTaskModal(true);
        break;
      case 'add-note':
        setShowNoteModal(true);
        break;
      default:
        console.log(`Modal type ${modalType} not yet implemented`);
    }
  };

  if (isLoading) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Contacts">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DetailPageLayout>
    );
  }

  if (!contact) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Contacts">
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Contact not found</p>
        </div>
      </DetailPageLayout>
    );
  }

  // Status badge colors
  const statusColors: Record<string, string> = {
    Active: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    Inactive: 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300'
  };

  const isOverview = ['overview', 'tab_001', 'details', 'notes'].includes(activeTab);
  const isActivity = ['activity', 'tab_003'].includes(activeTab);
  const isTasks = ['tasks'].includes(activeTab);
  const isProducts = ['products'].includes(activeTab);
  const isFiles = ['files', 'tab_004'].includes(activeTab);
  const isHistory = ['history'].includes(activeTab);

  return (
    <DetailPageLayout onBack={onBack} backLabel="Back to Contacts">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <DetailHeader
          title={contact.name as string}
          subtitle={`${contact.title || 'Contact'} at ${contact.account}`}
          recordId={contact.id as string}
          status={contact.status as string}
          statusColor={statusColors[contact.status as string] || statusColors.Active}
          onEdit={handleEdit}
          onDelete={handleDelete}
          objectName="Contact__c"
          recordData={contact as Record<string, any>}
          onOpenModal={handleOpenModal}
        />

        {/* Tabs */}
        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <DetailTabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
          />

          <div className="p-6">
            {isOverview && (
              <div className="space-y-6">
                {loadingLayout ? (
                  // Loading state
                  <div className="space-y-6">
                    {[1, 2, 3].map(i => (
                      <div
                        key={i}
                        className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6"
                      >
                        <div className="animate-pulse space-y-3">
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Render sections from page layout
                  pageLayout?.sections.map(section => (
                    <LayoutSection
                      key={section.id}
                      section={section}
                      objectApiName="Contact"
                      record={contact}
                    />
                  ))
                )}
              </div>
            )}

            {isActivity && (
              <ActivityTab
                key={activityRefreshKey}
                recordId={contact.id as string}
                recordType="contact"
                currentUserId={userId}
                onLogActivity={(type) => {
                  if (type === 'call') setShowCallModal(true);
                  if (type === 'meeting') setShowMeetingModal(true);
                  if (type === 'task') setShowTaskModal(true);
                  if (type === 'note') setShowNoteModal(true);
                }}
                onEditActivity={handleEditActivity}
                onDeleteActivity={handleDeleteActivity}
              />
            )}

            {isTasks && (
              <TasksTab
                relatedToId={contact.id as string}
                relatedToName={`${contact.firstName} ${contact.lastName}` || contact.name as string}
                relatedToType="Contact"
              />
            )}
            {isProducts && isFeatureEnabled('showProductsTab') && <ProductsTab />}
            {isFiles && isFeatureEnabled('showFilesTab') && <FilesTab />}
            {isHistory && <HistoryTab recordId={id} objectType="Contact__c" />}
          </div>
        </div>
      </div>

      {/* Contact Edit Modal */}
      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        contact={contact}
      />

      {/* Send Email Modal */}
      <SendEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        recordId={contact.id as string}
        recordType="contact"
        recipientEmail={contact.email as string}
        recipientName={contact.name as string}
      />

      {/* Log Call Modal */}
      <LogCallModal
        isOpen={showCallModal}
        onClose={() => setShowCallModal(false)}
        recordId={contact.id as string}
        recordType="contact"
        contactName={contact.name as string}
        onCallLogged={handleActivityChanged}
      />

      {/* Log Meeting Modal */}
      <LogMeetingModal
        isOpen={showMeetingModal}
        onClose={() => setShowMeetingModal(false)}
        recordId={contact.id as string}
        recordType="contact"
        onMeetingLogged={handleActivityChanged}
      />

      {/* Log Task Modal */}
      <LogTaskModal
        isOpen={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        recordId={contact.id as string}
        recordType="contact"
        contactName={contact.name as string}
        onTaskLogged={handleActivityChanged}
      />

      {/* Add Note Modal */}
      <AddNoteModal
        isOpen={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        recordId={contact.id as string}
        recordType="contact"
        onNoteAdded={handleActivityChanged}
      />

      {/* Edit Activity Modal */}
      <EditActivityModal
        isOpen={!!editingActivity}
        onClose={() => setEditingActivity(null)}
        activity={editingActivity}
        onActivityUpdated={handleActivityChanged}
      />

      {/* Delete Activity Confirmation */}
      {deletingActivity && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setDeletingActivity(null)}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Delete Activity?
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Are you sure you want to delete this {deletingActivity.type}? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeletingActivity(null)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteActivity}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DetailPageLayout>
  );
};
